/**
 * LegalLayout — Shared layout wrapper for legal pages
 *
 * Provides consistent structure for Terms of Service, Privacy Policy,
 * and Accessibility Statement pages.
 *
 * FS-016 WP-2 + RWP-B.
 */
import { useNavigate, Link } from "react-router-dom";

interface LegalLayoutProps {
  /** Page title displayed in the header */
  title: string;
  /** Human-readable date string for the "Last Updated" badge */
  lastUpdated: string;
  /** Optional HTML id for the page container */
  id?: string;
  /** Legal page content rendered inside the prose wrapper */
  children: React.ReactNode;
}

/** Chevron left icon for back button */
function ChevronLeftIcon() {
  return (
    <svg
      className="chevron-icon"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

/** Calendar icon for last-updated badge */
function CalendarIcon() {
  return (
    <svg
      className="calendar-icon"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

/** Arrow up icon for back-to-top button */
function ArrowUpIcon() {
  return (
    <svg
      className="arrow-up-icon"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 10l7-7m0 0l7 7m-7-7v18"
      />
    </svg>
  );
}

export function LegalLayout({
  title,
  lastUpdated,
  id,
  children,
}: LegalLayoutProps) {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="legal-page" id={id}>
      {/* Brand header with link back to app */}
      <div className="legal-brand-header" id="legal-brand-header">
        <Link
          to="/"
          className="legal-brand-link"
          aria-label="RetireEZ — Back to app"
        >
          RetireEZ
        </Link>
      </div>

      {/* Header with back button and last-updated badge */}
      <div className="legal-page-header">
        <button
          onClick={() => navigate(-1)}
          className="legal-back-button"
          aria-label="Go back"
        >
          <ChevronLeftIcon />
          Back
        </button>

        <span className="legal-last-updated">
          <CalendarIcon />
          {lastUpdated}
        </span>
      </div>

      {/* Page title */}
      <h1 className="legal-page-title">{title}</h1>

      {/* Prose content */}
      <div className="legal-prose">{children}</div>

      {/* Back to top button */}
      <button
        onClick={scrollToTop}
        className="legal-back-to-top"
        aria-label="Back to top"
        type="button"
      >
        <ArrowUpIcon />
        Back to Top
      </button>

      {/* Footer */}
      <footer className="legal-page-footer">
        <p>RetireEZ — Canadian Retirement Planning Made Easy</p>
      </footer>
    </div>
  );
}
